import { createWorker, OEM, type Worker as TesseractWorker } from 'tesseract.js';
import {
  FALLBACK_CONFIDENCE_THRESHOLD,
  LOW_RESOLUTION_NOTICE,
  MIN_RELIABLE_TEXT_HEIGHT_PX,
  isDevanagariOnly,
  tessdataDirFor,
  tesseractLangsFor,
  type LanguageId,
} from '../constants';
import type { VariantId } from '../imageOps';
import { POSTER_VARIANTS } from '../imageOps';
import {
  prepareOcrCanvas,
  releaseCanvas,
  type DecodedSource,
  type PrepareOptions,
} from '../preprocess';
import { medianTokenHeight, reconstructLayout, type LayoutResult, type OcrToken } from '../layout';
import { pickStrongest, scoreCandidate } from '../scoring';
import {
  OcrError,
  averageOf,
  toOcrError,
  type OcrLine,
  type OcrResult,
  type OcrRunContext,
  type StatusCallback,
} from '../types';

const PSM_AUTO = '3';
const PSM_SINGLE_BLOCK = '6';
const PSM_SPARSE = '11';

function langPath(langs: string[]): string {
  const base = process.env.PUBLIC_URL || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${base}/${tessdataDirFor(langs)}`;
}

interface WorkerHandle {
  key: string;
  worker: TesseractWorker;
  langs: string[];
  modelSource: string;
}

let handle: WorkerHandle | null = null;
let initPromise: Promise<WorkerHandle> | null = null;
/** Serialises every call so a single worker never runs two recognitions. */
let chain: Promise<unknown> = Promise.resolve();

function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = chain.then(fn, fn);
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

export function getLoadedClassicModel(): { langs: string[]; modelSource: string } | null {
  return handle ? { langs: handle.langs, modelSource: handle.modelSource } : null;
}

export async function terminateClassicWorker(): Promise<void> {
  initPromise = null;
  const current = handle;
  handle = null;
  if (current) {
    try {
      await current.worker.terminate();
    } catch (e) {
      /* worker already gone */
    }
  }
}

async function ensureWorker(languageId: LanguageId, onStatus?: StatusCallback): Promise<WorkerHandle> {
  const langs = tesseractLangsFor(languageId);
  const key = langs.join('+');
  if (handle && handle.key === key) return handle;
  if (initPromise) {
    const pending = await initPromise;
    if (pending.key === key) return pending;
  }
  // Never reuse a worker trained for a different language configuration.
  await terminateClassicWorker();

  const path = langPath(langs);
  initPromise = (async () => {
    onStatus?.({ text: `Loading ${key} language data`, progress: 0.02 });
    let worker: TesseractWorker;
    try {
      worker = await createWorker(langs, OEM.LSTM_ONLY, {
        langPath: path,
        gzip: true,
        // Reads from IndexedDB first, then writes newly fetched models back.
        cacheMethod: 'write',
        logger: (m) => {
          if (m.status === 'loading language traineddata') {
            onStatus?.({ text: `Loading ${key} language data`, progress: 0.02 + m.progress * 0.2 });
          } else if (m.status === 'initializing api' || m.status === 'initialized api') {
            onStatus?.({ text: 'Preparing recognition engine', progress: 0.25 });
          } else if (m.status === 'recognizing text') {
            onStatus?.({ text: 'Recognising text', progress: 0.3 + m.progress * 0.6 });
          }
        },
      });
    } catch (e) {
      const raw = typeof e === 'string' ? e : (e as Error)?.message || String(e);
      throw new OcrError(
        /network|fetch|traineddata|status/i.test(raw) ? 'MODEL_DOWNLOAD_FAILED' : 'WORKER_INIT_FAILED',
        `The Classic OCR engine could not load the ${key} model. Check your connection and retry.`,
        raw,
        'classic',
      );
    }
    const created: WorkerHandle = { key, worker, langs, modelSource: path };
    handle = created;
    return created;
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
}

/** Word-level tokens with bounding boxes, in OCR-canvas coordinates. */
function extractTokens(data: any, pageNumber: number): OcrToken[] {
  const tokens: OcrToken[] = [];
  const blocks = Array.isArray(data?.blocks) ? data.blocks : [];
  blocks.forEach((block: any) => {
    (block?.paragraphs || []).forEach((para: any) => {
      (para?.lines || []).forEach((line: any) => {
        const words = line?.words || [];
        if (words.length) {
          words.forEach((word: any) => {
            const text = (word?.text || '').trim();
            if (!text) return;
            const b = word.bbox || {};
            tokens.push({
              text,
              confidence: typeof word.confidence === 'number' ? word.confidence : null,
              boundingBox: {
                x: b.x0 ?? 0,
                y: b.y0 ?? 0,
                width: Math.max(1, (b.x1 ?? 0) - (b.x0 ?? 0)),
                height: Math.max(1, (b.y1 ?? 0) - (b.y0 ?? 0)),
              },
              page: pageNumber,
              engine: 'classic',
            });
          });
          return;
        }
        const text = (line?.text || '').trim();
        if (!text) return;
        const b = line.bbox || {};
        tokens.push({
          text,
          confidence: typeof line.confidence === 'number' ? line.confidence : null,
          boundingBox: {
            x: b.x0 ?? 0,
            y: b.y0 ?? 0,
            width: Math.max(1, (b.x1 ?? 0) - (b.x0 ?? 0)),
            height: Math.max(1, (b.y1 ?? 0) - (b.y0 ?? 0)),
          },
          page: pageNumber,
          engine: 'classic',
        });
      });
    });
  });
  return tokens;
}

interface Pass {
  variant: VariantId;
  psm: string;
  tokens: OcrToken[];
  layout: LayoutResult;
  averageConfidence: number | null;
  inverted: boolean;
  scale: number;
  candidate: { text: string; averageConfidence: number | null; lineCount: number };
}

async function runPass(
  worker: TesseractWorker,
  source: DecodedSource,
  prepare: PrepareOptions,
  variant: VariantId,
  psm: string,
  pageNumber: number,
): Promise<Pass> {
  let canvas: HTMLCanvasElement | null = null;
  try {
    const prepared = prepareOcrCanvas(source, { ...prepare, variant });
    canvas = prepared.canvas;
    await worker.setParameters({
      tessedit_pageseg_mode: psm as never,
      preserve_interword_spaces: '1',
      user_defined_dpi: '300',
    });
    const { data } = await worker.recognize(canvas, {}, { text: true, blocks: true });
    const tokens = extractTokens(data, pageNumber);
    const layout = reconstructLayout(tokens);
    const averageConfidence = averageOf(
      tokens.map((t) => t.confidence).filter((c): c is number => c !== null),
    );
    return {
      variant,
      psm,
      tokens,
      layout,
      averageConfidence,
      inverted: prepared.meta.inverted,
      scale: prepared.scale,
      candidate: {
        text: layout.text,
        averageConfidence,
        lineCount: layout.lines.length,
      },
    };
  } finally {
    releaseCanvas(canvas);
  }
}

const PSM_LABEL: Record<string, string> = {
  [PSM_AUTO]: 'automatic page segmentation (PSM 3)',
  [PSM_SINGLE_BLOCK]: 'single uniform block (PSM 6)',
  [PSM_SPARSE]: 'sparse text (PSM 11)',
};

export interface ClassicRecognizeInput {
  source: DecodedSource;
  prepare: PrepareOptions;
}

/**
 * Recognises one image source with Tesseract only. Multiple non-destructive
 * preprocessing/segmentation passes may run; the strongest single pass is
 * selected by score. Passes are never concatenated.
 */
export async function classicRecognize(
  input: ClassicRecognizeInput,
  ctx: OcrRunContext,
  onStatus?: StatusCallback,
): Promise<OcrResult> {
  const started = Date.now();
  const langs = tesseractLangsFor(ctx.languageId);
  const workerHandle = await ensureWorker(ctx.languageId, onStatus);
  if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');

  return serial(async () => {
    if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
    const worker = workerHandle.worker;
    const warnings: string[] = [];
    const pageNumber = ctx.pageNumber ?? 1;
    const expectDevanagari = isDevanagariOnly(ctx.languageId);
    const scoreOptions = { expectDevanagari };

    try {
      const passes: Pass[] = [];
      const run = async (variant: VariantId, psm: string, label: string, progress: number) => {
        if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
        onStatus?.({ text: label, progress });
        passes.push(await runPass(worker, input.source, input.prepare, variant, psm, pageNumber));
      };

      if (ctx.mode === 'poster') {
        // Four non-destructive variants, single uniform block first.
        for (let i = 0; i < POSTER_VARIANTS.length; i += 1) {
          const variant = POSTER_VARIANTS[i];
          // eslint-disable-next-line no-await-in-loop
          await run(variant, PSM_SINGLE_BLOCK, `Trying ${variant} pass`, 0.3 + i * 0.12);
        }
        const leader = pickStrongest(passes, scoreOptions);
        if (leader) {
          await run(leader.entry.variant, PSM_SPARSE, 'Trying sparse-text segmentation', 0.82);
          await run(leader.entry.variant, PSM_AUTO, 'Trying automatic segmentation', 0.9);
        }
      } else {
        await run('upscaled', PSM_AUTO, 'Recognising text', 0.4);
        const first = passes[0];
        const weak =
          !first.candidate.text.trim() ||
          (first.averageConfidence ?? 0) < FALLBACK_CONFIDENCE_THRESHOLD ||
          (expectDevanagari && scoreCandidate(first.candidate, scoreOptions).devanagariRatio < 0.6);
        if (weak) {
          await run('grayscale-contrast', PSM_AUTO, 'Trying an enhanced variant', 0.7);
          await run('adaptive-threshold', PSM_SINGLE_BLOCK, 'Trying a high-contrast variant', 0.85);
        }
      }

      const best = pickStrongest(passes, scoreOptions);
      if (!best) throw new OcrError('NO_TEXT_FOUND', 'No readable text was detected.');
      if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');

      const winner = best.entry;
      const text = winner.layout.text.normalize('NFC');
      if (!text.trim()) {
        throw new OcrError(
          'NO_TEXT_FOUND',
          'No readable text was detected. Try the other recognition mode, crop closer, or use a sharper image.',
          undefined,
          'classic',
        );
      }

      if (passes.length > 1) {
        warnings.push(
          `Compared ${passes.length} preprocessing/segmentation passes and kept the strongest one (${winner.variant}, ${PSM_LABEL[winner.psm]}).`,
        );
      }
      if (winner.layout.tables.length) {
        warnings.push(
          `${winner.layout.tables.length} table-like block${
            winner.layout.tables.length > 1 ? 's' : ''
          } reconstructed into rows and columns.`,
        );
      }

      const canvasTextHeight = medianTokenHeight(winner.tokens);
      const medianSourceTextHeight = winner.scale > 0 ? canvasTextHeight / winner.scale : null;
      const lowResolution =
        medianSourceTextHeight !== null && medianSourceTextHeight < MIN_RELIABLE_TEXT_HEIGHT_PX;
      if (lowResolution) warnings.push(LOW_RESOLUTION_NOTICE);

      const lines: OcrLine[] = winner.layout.lines.map((line) => ({
        text: line.text.normalize('NFC'),
        confidence: line.confidence,
        bbox: {
          x0: Math.min(...line.tokens.map((t) => t.boundingBox.x)),
          y0: line.top,
          x1: Math.max(...line.tokens.map((t) => t.boundingBox.x + t.boundingBox.width)),
          y1: line.bottom,
        },
        pageNumber,
      }));

      return {
        text,
        lines,
        tokens: winner.tokens,
        tables: winner.layout.tables,
        averageConfidence: winner.averageConfidence,
        engine: 'classic',
        engineLabel: 'Classic OCR · Tesseract LSTM',
        languageId: ctx.languageId,
        languageConfig: workerHandle.langs,
        modelSource: workerHandle.modelSource,
        mode: ctx.mode,
        preprocessVariant: winner.variant,
        segmentationMode: PSM_LABEL[winner.psm] || `PSM ${winner.psm}`,
        durationMs: Date.now() - started,
        pageNumber: ctx.pageNumber ?? null,
        invertedForDarkBackground: winner.inverted,
        medianSourceTextHeight,
        lowResolution,
        warnings,
      };
    } catch (e) {
      const err = toOcrError(e);
      err.engine = 'classic';
      throw err;
    }
  });
}

export { langPath as classicLangPath };
